import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Kpis, Modal, Segments, Empty, useFlash, useModal } from '../../ui/kit.js';
import { useBuilding, clock } from '../../state/building.js';
import { DoorActions, DoorCard, DoorDetail, isOpenState } from '../../ui/access/doors.js';
import { EventFeed } from '../../ui/access/feed.js';
import { Credentials } from '../../ui/access/credentials.js';
import { Schedules } from '../../ui/access/schedules.js';
import { Icon } from '../../ui/access/glyphs.js';
import { Elapsed, Toast } from '../../ui/access/parts.js';
import { simulate } from '../../ui/access/sim.js';
import { codeStatus, doorState, freePassageNow, scheduleText, timeOf } from '../../ui/access/model.js';

const EASE = [0.16, 1, 0.3, 1];

/* What every resident screen shows while the building is locked down. */
const LOCKDOWN_ALERT = {
    level: 'emergency',
    title: 'Building lockdown in effect',
    body: 'Stay inside your residence. Doors are secured by the front desk.'
};

/* Denials from before the feed's window, so "today" is a day's worth. */
const DENIED_EARLIER = 5;

const AREAS = {
    All: () => true,
    Attention: (d, state) => state === 'held' || state === 'forced' || state === 'offline',
    Entrances: d => d.area === 'Entrances',
    Amenities: d => d.area === 'Amenities',
    'Back of house': d => d.area === 'Back of house'
};

/* Access Control: every door in the building on one screen, live.
   The grid is the building's doors as they are this second; the feed is
   what just happened at them; lockdown is the one control that matters
   most on the worst day, so it sits above both. Credentials and
   schedules follow, because they are what decides who the doors let in. */
function AccessControlScreen() {
    const {
        doors, accessEvents, credentials, guestCodes, schedules, holidayHours, lockdown, alert,
        patch, logAccess, pulseDoor, startLockdown, endLockdown, setAlert
    } = useBuilding();

    const reduced = useReducedMotion();
    const [flash, notify] = useFlash();
    const [streaming, setStreaming] = useState(true);
    const [feedFilter, setFeedFilter] = useState('All');
    const [area, setArea] = useState('All');
    const [reconnecting, setReconnecting] = useState([]);
    const detail = useModal();
    const confirm = useModal();
    const { open: openDetail } = detail;
    const timers = useRef([]);

    useEffect(() => () => timers.current.forEach(clearTimeout), []);

    /* The building's background traffic. The tick reads the records
       through a ref so a new event does not restart the timer. */
    const live = useRef(null);
    live.current = { doors, credentials, guestCodes, lockdown, schedules, holidayHours };

    useEffect(() => {
        if (!streaming) return undefined;
        let timer;
        const tick = () => {
            for (const op of simulate(live.current)) {
                if (op.event) logAccess(op.event, op.changes);
                if (op.code) patch('guestCodes', op.code, op.codeChanges);
            }
            timer = setTimeout(tick, 3000 + Math.random() * 3000);
        };
        timer = setTimeout(tick, 1400);
        return () => clearTimeout(timer);
    }, [streaming, logAccess, patch]);

    const states = useMemo(() => {
        const map = {};
        doors.forEach(d => { map[d.id] = doorState(d, { lockdown, schedules, holiday: holidayHours }); });
        return map;
    }, [doors, lockdown, schedules, holidayHours]);

    /* One handler for every door control, so a card can be memoised. */
    const act = useCallback((what, door) => {
        const { doors: current, schedules: plan, holidayHours: holiday } = live.current;
        const latest = current.find(d => d.id === door.id) || door;
        const state = doorState(latest, { lockdown: live.current.lockdown, schedules: plan, holiday });
        const freePassage = freePassageNow(latest, plan, holiday);

        if (what === 'open') return openDetail(door.id);

        if (what === 'pulse') {
            pulseDoor(door.id, { who: 'Front desk', via: 'Remote unlock' });
            return notify(`${door.name} unlocked for 5 seconds.`);
        }

        if (what === 'toggle') {
            if (state === 'unlocked') {
                logAccess({ door: door.id, kind: 'system', tone: 'ok', label: 'Locked', title: 'Locked by front desk', who: 'Front desk', via: 'Override' },
                    { override: freePassage ? 'locked' : null });
                return notify(`${door.name} locked.${freePassage ? ' It stays locked until you resume its schedule.' : ''}`);
            }
            logAccess({ door: door.id, kind: 'granted', tone: 'info', label: 'Override', title: 'Kept unlocked by front desk', who: 'Front desk', via: 'Override' },
                { override: freePassage ? null : 'unlocked' });
            return notify(`${door.name} stays unlocked until you lock it.`);
        }

        if (what === 'resume') {
            logAccess({ door: door.id, kind: 'system', tone: 'info', label: 'Schedule', title: 'Back on schedule', who: 'Front desk', via: 'Override cleared' }, { override: null });
            return notify(`${door.name} is back on its schedule.`);
        }

        if (what === 'ack') {
            logAccess({
                door: door.id, kind: 'alarm', tone: 'ok', label: 'Cleared', title: 'Alarm acknowledged',
                who: 'Front desk', via: state === 'forced' ? 'Security dispatched' : 'Door closed'
            }, { status: 'ok', since: 0, holdFor: 0 });
            return notify(state === 'forced'
                ? `${door.name} alarm acknowledged. Marcus Reed is on his way to check it.`
                : `${door.name} alarm acknowledged and the door is closed.`);
        }

        if (what === 'reconnect') {
            setReconnecting(list => [...list, door.id]);
            timers.current.push(setTimeout(() => {
                setReconnecting(list => list.filter(id => id !== door.id));
                logAccess({ door: door.id, kind: 'system', tone: 'ok', label: 'Online', title: 'Reader back online', who: 'Controller 3', via: 'Network' }, { status: 'ok', since: 0 });
                notify(`${door.name} reader is back online and locked.`);
            }, 1800));
        }
        return undefined;
    }, [openDetail, logAccess, notify, pulseDoor]);

    const beginLockdown = () => {
        confirm.close();
        startLockdown('Front desk');
        setAlert({ ...LOCKDOWN_ALERT, sentAt: clock() });
        notify('Lockdown in effect. Every door is locked and every resident screen shows the alert.');
    };

    const finishLockdown = () => {
        endLockdown('Front desk');
        /* Clear the strip only if it is still ours; a newer alert sent
           from Emergency during the lockdown stays up. */
        if (!alert || alert.title === LOCKDOWN_ALERT.title) setAlert(null);
        notify('Lockdown ended. Doors are back on their schedules and the resident alert is cleared.');
    };

    /* Figures for the strip. */
    const values = Object.values(states);
    const online = values.filter(s => s !== 'offline').length;
    const open = values.filter(isOpenState).length;
    const held = values.filter(s => s === 'held' || s === 'forced').length;
    const denied = DENIED_EARLIER + accessEvents.filter(e => e.kind === 'denied').length;
    const lastDenied = accessEvents.find(e => e.kind === 'denied');
    const activeCreds = credentials.filter(c => c.status === 'Active').length;
    const activeCodes = guestCodes.filter(c => codeStatus(c) === 'Active').length;
    const attention = doors.filter(d => AREAS.Attention(d, states[d.id])).length;
    const offlineDoors = doors.filter(d => states[d.id] === 'offline');

    const shownDoors = doors.filter(d => AREAS[area](d, states[d.id]));

    /* The sheet keeps showing its door while it animates closed. */
    const sheetId = useRef(null);
    if (detail.isOpen) sheetId.current = detail.item;
    const openDoor = doors.find(d => d.id === sheetId.current) || null;

    return (
        <div className="screen-content acc-screen">
            <ScreenHead
                eyebrow="Access control"
                title="Doors and credentials"
                aside={
                    <span className={`acc-sys${lockdown ? ' acc-sys--lockdown' : ''}`}>
                        <span className="acc-sys__dot" />
                        {lockdown ? 'Lockdown active' : `${online} of ${doors.length} readers online`}
                    </span>
                }
            />
            <Toast>{flash}</Toast>

            <AnimatePresence mode="wait" initial={false}>
                {lockdown ? (
                    <motion.section
                        key="on"
                        className="acc-lockdown acc-lockdown--on"
                        role="status"
                        initial={reduced ? false : { opacity: 0, y: -10, scale: 0.99 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.5, ease: EASE }}
                    >
                        <span className="acc-lockdown__pulse" aria-hidden="true" />
                        <span className="acc-lockdown__icon"><Icon name="shield" size={26} /></span>
                        <div className="acc-lockdown__text">
                            <span className="acc-lockdown__eyebrow">Lockdown in effect · started {timeOf(lockdown.since)} by {lockdown.by}</span>
                            <strong className="acc-lockdown__title">
                                {offlineDoors.length
                                    ? `${doors.length - offlineDoors.length} of ${doors.length} doors secured`
                                    : `All ${doors.length} doors secured`}
                            </strong>
                            <span className="acc-lockdown__body">
                                Mobile keys, resident fobs and visitor codes are refused. Every resident screen shows the lockdown alert.
                                {offlineDoors.length ? ` ${offlineDoors.map(d => d.name).join(', ')} reader is offline: send someone to check it.` : ''}
                            </span>
                        </div>
                        <div className="acc-lockdown__side">
                            <span className="acc-lockdown__clock"><Elapsed since={lockdown.since} /></span>
                            <span className="acc-lockdown__label">elapsed</span>
                            <button type="button" className="btn acc-btn-light" onClick={finishLockdown}>End lockdown</button>
                        </div>
                    </motion.section>
                ) : (
                    <motion.section
                        key="off"
                        className="acc-lockdown"
                        initial={reduced ? false : { opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.5, ease: EASE }}
                    >
                        <span className="acc-lockdown__icon"><Icon name="shield" size={24} /></span>
                        <div className="acc-lockdown__text">
                            <strong className="acc-lockdown__title">Building lockdown</strong>
                            <span className="acc-lockdown__body">One tap locks all {doors.length} doors, pauses every schedule, mobile key and visitor code, and alerts every resident screen.</span>
                        </div>
                        <button type="button" className="btn acc-btn-danger" onClick={() => confirm.open(true)}>
                            <Icon name="shield" size={16} /> Lockdown building
                        </button>
                    </motion.section>
                )}
            </AnimatePresence>

            <Kpis items={[
                { label: 'Doors online', value: `${online}/${doors.length}`, tone: online === doors.length ? 'ok' : 'warn', note: online === doors.length ? 'All readers reporting' : `${doors.length - online} reader offline` },
                { label: 'Unlocked or held open', value: open, tone: held ? 'warn' : 'info', note: held ? `${held} in alarm` : 'No alarms' },
                { label: 'Denied today', value: denied, tone: 'alert', note: lastDenied ? `Last: ${lastDenied.doorName}, ${timeOf(lastDenied.ts)}` : 'Since midnight' },
                { label: 'Active credentials', value: activeCreds, note: `${activeCodes} visitor codes active` }
            ]} />

            <div className="acc-main">
                <section className="acc-doors" aria-label="Doors">
                    <div className="acc-toolbar">
                        <h4>Doors</h4>
                        <Segments
                            label="Show doors"
                            value={area}
                            onChange={setArea}
                            options={Object.keys(AREAS).map(key => ({ value: key, label: key === 'Attention' ? `Attention ${attention}` : key }))}
                        />
                    </div>

                    <div className={`acc-grid${lockdown ? ' acc-grid--lockdown' : ''}`}>
                        <AnimatePresence initial={false} mode="popLayout">
                            {shownDoors.map(door => (
                                <DoorCard
                                    key={door.id}
                                    door={door}
                                    state={states[door.id]}
                                    hours={scheduleText(door, schedules, holidayHours)}
                                    freePassage={freePassageNow(door, schedules, holidayHours)}
                                    lockdown={!!lockdown}
                                    reconnecting={reconnecting.includes(door.id)}
                                    act={act}
                                />
                            ))}
                        </AnimatePresence>
                    </div>
                    {shownDoors.length === 0 && <Empty>No doors need attention right now.</Empty>}
                </section>

                <EventFeed
                    index={2}
                    events={accessEvents}
                    running={streaming}
                    onToggle={() => setStreaming(s => !s)}
                    filter={feedFilter}
                    onFilter={setFeedFilter}
                />
            </div>

            <div className="section-gap">
                <Credentials notify={notify} />
            </div>

            <div className="section-gap">
                <Schedules notify={notify} />
            </div>

            <Modal
                open={detail.isOpen && !!openDoor}
                onClose={detail.close}
                size="lg"
                eyebrow={openDoor ? `${openDoor.area} · ${openDoor.zone}` : ''}
                title={openDoor ? openDoor.name : ''}
                actions={openDoor && (
                    <>
                        <DoorActions
                            door={openDoor}
                            state={states[openDoor.id]}
                            freePassage={freePassageNow(openDoor, schedules, holidayHours)}
                            lockdown={!!lockdown}
                            reconnecting={reconnecting.includes(openDoor.id)}
                            act={act}
                        />
                        <button type="button" className="btn btn-outline-light" onClick={detail.close}>Close</button>
                    </>
                )}
            >
                {openDoor && (
                    <DoorDetail
                        door={openDoor}
                        state={states[openDoor.id]}
                        events={accessEvents}
                        schedules={schedules}
                        holiday={holidayHours}
                        hours={scheduleText(openDoor, schedules, holidayHours)}
                    />
                )}
            </Modal>

            <Modal
                open={confirm.isOpen}
                onClose={confirm.close}
                dismissable={false}
                eyebrow="Security"
                title="Lock down the building?"
                actions={
                    <>
                        <button type="button" className="btn btn-outline-light" onClick={confirm.close}>Cancel</button>
                        <button type="button" className="btn acc-btn-danger" onClick={beginLockdown}>
                            <Icon name="shield" size={16} /> Lock down now
                        </button>
                    </>
                }
            >
                <p>Use this for an active threat inside or near the building. It takes effect at once.</p>
                <ul className="acc-consequences">
                    <li><Icon name="door" size={18} /><span><strong>All {doors.length} doors lock</strong> and schedules, free passage and desk overrides pause.</span></li>
                    <li><Icon name="key" size={18} /><span><strong>Resident fobs, mobile keys and visitor codes</strong> stop working. Security staff credentials still open doors.</span></li>
                    <li><Icon name="alert" size={18} /><span><strong>Every resident screen</strong> shows an emergency alert telling residents to stay inside.</span></li>
                </ul>
                <p className="acc-faint">This does not call 911. Call emergency services first if anyone is in danger.</p>
            </Modal>
        </div>
    );
}

export default AccessControlScreen;
